import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { useLocale } from '../intl/hooks';

const defaultOptions = {
  messageProp: 'messageDescriptor',
  labelProp: 'label',
  formatValuesProp: 'values',
  prioritySort: () => 0,
};

/**
 * Transforms an array of objects that each contain a message descriptor object into an array of objects that each
 *    contain a message descriptor AND a translated string
 * @param {object[]} values - array of objects that each contain a message descriptor
 * @param {object} options : object that optionally contains arguments to specify how to perform the translations
 *    messageProp - the property name for the message descriptor to read from
 *    formatValuesProp - the property name for any values needed for formatting
 *    labelProp - the property name for the translated string to be written to
 * @returns {object[]} - array of objects that each contain a message descriptor AND a translated string
 */
export const useTranslatedStrings = (values, options = {}) => {
  const { formatMessage } = useIntl();
  return useMemo(() => {
    const messageProp = options.messageProp ?? defaultOptions.messageProp;
    const formatValuesProp = options.formatValuesProp ?? defaultOptions.formatValuesProp;
    const labelProp = options.labelProp ?? defaultOptions.labelProp;

    const translatedValues = values.map((value) => {
      const { [messageProp]: formattedMessage, [formatValuesProp]: formattedMessageProps, ...rest } = value;
      const translatedValue = formattedMessage ? formatMessage(formattedMessage, formattedMessageProps) : '';
      return { ...rest, [labelProp]: translatedValue };
    });
    return translatedValues;
  }, [formatMessage, options.formatValuesProp, options.labelProp, options.messageProp, values]);
};

/**
 * Sorts an array of objects that each contain a translated string according to the locale of the user, additionally can
 *    include another sort to perform beforehand. (i.e., sort by 'type', then translated string value)
 * @param {object[]} translatedValues : array of objects that each contain a translated string
 * @param {object} options : object that optionally contains arguments to specify how to perform the translations
 *    labelProp - the property name for the translated string to be written to
 *    prioritySort - a function that can serve as a higher level sort priority
 * @returns {object[]} - array of objects that each contain a translated string
 */
export const useSortedStrings = (translatedValues, options) => {
  const { locale } = useLocale();
  return useMemo(() => {
    const labelProp = options.labelProp ?? defaultOptions.labelProp;
    const prioritySort = options.prioritySort ?? defaultOptions.prioritySort;
    const collator = new Intl.Collator(locale);
    const compare = (a, b) => prioritySort(a, b) || collator.compare(a[labelProp], b[labelProp]);

    return [...translatedValues].sort(compare);
  }, [locale, options.labelProp, options.prioritySort, translatedValues]);
};

/**
 * Performs 2 operations to the supplied values. First, performs a translation on each value according to a supplied
 *    message descriptor for each value. Second, it sorts those translated values based on the user's locale.
 * @param {object[]} values : array of objects that each contain a message descriptor
 * @param {object} options : object that optionally contains arguments to specify how to perform the translations
 *    messageProp - the property name for the message descriptor to read from
 *    formatValuesProp - the property name for any values needed for formatting
 *    labelProp - the property name for the translated string to be written to
 *    prioritySort - a function that can serve as a higher level sort priority
 * @returns {object[]} - array of objects that each contain a translated string
 */
export const useSortedTranslations = (values, options) => {
  const translatedValues = useTranslatedStrings(values, options);
  const sortedTranslations = useSortedStrings(translatedValues, options);
  return sortedTranslations;
};
